/**
 * Единая структура админ-меню: используется в PanelLayout (сайдбар)
 * и в App (редирект /panel/admin на первый доступный раздел).
 * perm — право, необходимое для показа пункта (совпадает с гвардом бэкенда).
 *
 * Меню — смесь отдельных пунктов и раскрывающихся групп. Порядок узлов
 * задаёт и порядок в сайдбаре, и приоритет редиректа /panel/admin.
 */
export interface AdminNavEntry {
  to: string;
  label: string;
  /** Ключ иконки — маппится на SVG в PanelLayout */
  icon: string;
  /** Массив = достаточно любого из прав, как requireAnyPermission на бэкенде */
  perm?: string | string[];
}

export type AdminNavNode =
  | { kind: 'item'; entry: AdminNavEntry }
  /** Подпись-разделитель в меню (например, «Разделы») */
  | { kind: 'label'; text: string }
  | {
      kind: 'group';
      /** Стабильный id — ключ localStorage для запоминания раскрытия */
      id: string;
      label: string;
      icon: string;
      entries: AdminNavEntry[];
    };

const item = (entry: AdminNavEntry): AdminNavNode => ({ kind: 'item', entry });

// Порядок как в утверждённом макете: частые разделы наверху, остальное — свёрнутыми группами
export const adminNavTree: AdminNavNode[] = [
  item({ to: '/panel/admin/dashboard', label: 'Главная',   icon: 'dashboard', perm: 'dashboard.view' }),
  item({ to: '/panel/admin/tickets',   label: 'Поддержка', icon: 'tickets',   perm: ['tickets.view', 'support.flow.manage'] }),
  item({ to: '/panel/admin/chat',      label: 'Чат',       icon: 'chat',      perm: 'chat.access' }),
  item({ to: '/panel/admin/tasks',     label: 'Задачи',    icon: 'tasks',     perm: 'tasks.view' }),
  item({ to: '/panel/admin/server',    label: 'Сервер',    icon: 'server',    perm: 'rcon.view' }),
  { kind: 'label', text: 'Разделы' },
  {
    kind: 'group', id: 'players', label: 'Игроки', icon: 'users',
    entries: [
      { to: '/panel/admin/users',    label: 'Пользователи', icon: 'users',    perm: 'users.view' },
      { to: '/panel/admin/multiaccounts', label: 'Мультиаккаунты', icon: 'users', perm: ['multiacc.view', 'multiacc.manage'] },
      { to: '/panel/admin/bans',     label: 'Блокировки',   icon: 'errors',   perm: 'bans.manage' },
      { to: '/panel/admin/roles',    label: 'Роли и права', icon: 'users',    perm: 'access.manage' },
      { to: '/panel/admin/vehicles', label: 'Транспорт',    icon: 'vehicles', perm: 'vehicles.manage' },
      { to: '/panel/admin/fractions', label: 'Фракции',     icon: 'users',    perm: 'users.view' },
    ],
  },
  {
    kind: 'group', id: 'overview', label: 'Обзор', icon: 'stats',
    entries: [
      { to: '/panel/admin/stats',   label: 'Статистика', icon: 'stats',    perm: 'stats.view' },
      { to: '/panel/admin/online',  label: 'Онлайн',     icon: 'online',   perm: 'online.view' },
      { to: '/panel/admin/map',     label: 'Карта',      icon: 'map',      perm: 'map.view' },
      { to: '/panel/admin/finance', label: 'Финансы',    icon: 'finance',  perm: 'finance.view' },
    ],
  },
  {
    kind: 'group', id: 'content', label: 'Контент', icon: 'pages',
    entries: [
      { to: '/panel/admin/news',      label: 'Новости',   icon: 'pages',     perm: 'news.manage' },
      { to: '/panel/admin/pages',     label: 'Страницы',  icon: 'pages',     perm: 'pages.manage' },
      { to: '/panel/admin/rules',     label: 'Правила',   icon: 'rules',     perm: 'rules.manage' },
      { to: '/panel/admin/faq',       label: 'FAQ',       icon: 'rules',     perm: 'faq.manage' },
      { to: '/panel/admin/documents', label: 'Документы', icon: 'documents', perm: 'legal.manage' },
      { to: '/panel/admin/files',     label: 'Файлы',     icon: 'files',     perm: 'files.manage' },
    ],
  },
  // Раздел «Логи» — вкладки-журналы (LogsShell); пункт ведёт на последний открытый журнал
  item({ to: '/panel/admin/logs', label: 'Логи', icon: 'logs', perm: ['logs.site.view', 'logs.money.view', 'logs.vehicles.view', 'logs.resources.view', 'logs.territory.view', 'logs.messages.view', 'logs.casino.view', 'logs.events.view', 'logs.kills.view'] }),
  item({ to: '/panel/admin/launcher', label: 'Лаунчер', icon: 'launcher', perm: ['launcher.manage', 'launcher.users.view'] }),
  item({ to: '/panel/admin/promocodes', label: 'Промокоды', icon: 'promo', perm: 'promocodes.manage' }),
  {
    kind: 'group', id: 'system', label: 'Система', icon: 'settings',
    entries: [
      { to: '/panel/admin/settings',     label: 'Настройки',    icon: 'settings', perm: 'settings.manage' },
      { to: '/panel/admin/oidc-clients', label: 'OIDC-клиенты', icon: 'oidc',     perm: 'oidc.clients.manage' },
      { to: '/panel/admin/errors',       label: 'Ошибки',       icon: 'errors',   perm: ['errors.manage', 'errors.reports.view'] },
      { to: '/panel/admin/incidents',    label: 'Инциденты',    icon: 'errors',   perm: 'incidents.view' },
    ],
  },
];

/** Журналы логов — вкладки раздела «Логи» (пункта меню у них нет). */
export const adminLogJournals: AdminNavEntry[] = [
  { to: '/panel/admin/logs/site',        label: 'Действия на сайте', icon: 'logs', perm: 'logs.site.view' },
  { to: '/panel/admin/logs/auth',        label: 'Авторизация',       icon: 'logs', perm: 'logs.site.view' },
  { to: '/panel/admin/logs/money',       label: 'Денежные операции', icon: 'logs', perm: 'logs.money.view' },
  { to: '/panel/admin/logs/vehicles',    label: 'Транспорт',         icon: 'logs', perm: 'logs.vehicles.view' },
  { to: '/panel/admin/logs/resources',   label: 'Ресурсы',           icon: 'logs', perm: 'logs.resources.view' },
  { to: '/panel/admin/logs/bpla',        label: 'БПЛА',              icon: 'logs', perm: 'logs.vehicles.view' },
  { to: '/panel/admin/logs/momma-heart', label: 'Сердца Моммы',      icon: 'logs', perm: 'logs.resources.view' },
  { to: '/panel/admin/logs/territory',   label: 'Войны за территории', icon: 'logs', perm: 'logs.territory.view' },
  { to: '/panel/admin/logs/comms',       label: 'Общение',           icon: 'logs', perm: 'logs.messages.view' },
  { to: '/panel/admin/logs/casino',      label: 'Казино',            icon: 'logs', perm: 'logs.casino.view' },
  { to: '/panel/admin/logs/events',      label: 'События',           icon: 'logs', perm: 'logs.events.view' },
  { to: '/panel/admin/logs/kills',       label: 'Убийства',          icon: 'logs', perm: 'logs.kills.view' },
];

/** Плоский список пунктов в порядке меню (для редиректа и обратной совместимости). */
export const adminNavItems: AdminNavEntry[] = adminNavTree.flatMap(node =>
  node.kind === 'item' ? [node.entry] : node.kind === 'group' ? node.entries : [],
);

/** Хватает ли прав на пункт меню. */
export function canSeeAdminNavEntry(entry: AdminNavEntry, permissions: string[]): boolean {
  if (!entry.perm) return true;
  const needed = Array.isArray(entry.perm) ? entry.perm : [entry.perm];
  return needed.some(p => permissions.includes(p));
}

/** Первый доступный по правам раздел админки (для редиректа /panel/admin). */
export function firstAccessibleAdminPath(permissions: string[]): string {
  const item = adminNavItems.find(i => canSeeAdminNavEntry(i, permissions));
  return item?.to ?? '/panel/access-denied';
}
